import { Fragment, useState } from "react"
import {
	Alert,
	Box,
	Button,
	Dropdown,
	Icons,
	Inline,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	useDialog,
} from "@sembark-travel/ui-dialog"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { useXHR } from "@sembark-travel/xhr"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { IHotel } from "../Hotels"
import {
	HotelConfigActionRequiredAlert,
	UnlockHotelConfigInDialog,
} from "../shared/HotelConfigLock"
import {
	hotelOtaMealPlansXHR,
	refreshHotelOtaMealPlans,
	TOtaMealCategory,
	TOtaMealPlan,
} from "./store"
import { OtaMealPlanInDialog } from "./NewItem"

export function HotelOtaMealPlans({
	hotel,
	onChange,
}: {
	hotel: IHotel
	onChange: () => void
}) {
	const [params, setParams] = useSearch<Record<string, unknown>>()
	const { hasPermission } = useCheckPermissions()
	const canManage = hasPermission(PERMISSIONS.CREATE_HOTELS)
	const xhr = useXHR()
	const [isAddOpen, openAddDialog, closeAddDialog] = useDialog()

	const [freshKey, setRefreshKey] = useState(1)
	function handleChange() {
		setRefreshKey((prev) => prev + 1)
		refreshHotelOtaMealPlans(hotel.id)
		onChange()
	}
	return (
		<Fragment>
			<Search
				initialParams={params}
				onSearch={(newParams) => setParams({ ...newParams, page: 1 })}
				title="Meal Plans"
				placeholder="Search"
				actions={
					canManage ? (
						<UnlockHotelConfigInDialog
							title="Add Meal Plan"
							action="Add new Meal Plan"
							afterAction="adding a new Meal Plan"
							rateSheetStep="include the new Meal Plan"
							acceptLabel="Yes, unlock & add Meal Plan"
							hasChannelManager={Boolean(hotel.channel_manager)}
							onSuccess={openAddDialog}
						>
							{({ open }) => (
								<Button level="primary" onClick={open}>
									<Icons.Lock /> Add Meal Plan
								</Button>
							)}
						</UnlockHotelConfigInDialog>
					) : null
				}
			>
				<ListView<TOtaMealPlan>
					pageKey={`hotel-${hotel.id}-ota-meal-plans-${freshKey}`}
					fetch={(xhr, params) =>
						hotelOtaMealPlansXHR(xhr).getOtaMealPlans(hotel.id, params)
					}
					params={params}
					onPageChange={(page) => setParams({ ...params, page })}
				>
					{({ items }) => (
						<OtaMealPlansTable
							mealPlans={items}
							renderActions={
								canManage
									? (mealPlan) => (
											<MealPlanRowActions
												hotel={hotel}
												mealPlan={mealPlan}
												onChange={handleChange}
											/>
										)
									: undefined
							}
						/>
					)}
				</ListView>
			</Search>
			{canManage ? (
				<OtaMealPlanInDialog
					notice={
						<HotelConfigActionRequiredAlert
							afterAction="adding this Meal Plan"
							rateSheetStep="include this new Meal Plan"
							hasChannelManager={Boolean(hotel.channel_manager)}
						/>
					}
					open={isAddOpen}
					onClose={closeAddDialog}
					onSubmit={async (data) => {
						await hotelOtaMealPlansXHR(xhr).createOtaMealPlan(hotel.id, data)
						showSnackbar("Meal plan added successfully")
						closeAddDialog()
						handleChange()
					}}
				/>
			) : null}
		</Fragment>
	)
}

export function OtaMealCategoryBadge({
	category,
}: {
	category: TOtaMealCategory | null
}) {
	return (
		<Box
			paddingY="1"
			paddingX="2"
			bgColor={category ? "accent" : "inset"}
			rounded="md"
			fontSize="xs"
			color={category ? "accent" : "muted"}
		>
			{category || "None"}
		</Box>
	)
}

// Saved meal plans have an `id`; the ones only kept in a form don't.
export type TOtaMealPlansTableItem = Pick<
	TOtaMealPlan,
	"name" | "description" | "meal_category"
> & { id?: number }

export function OtaMealPlansTable<Item extends TOtaMealPlansTableItem>({
	mealPlans,
	renderActions,
}: {
	mealPlans: Array<Item>
	renderActions?: (mealPlan: Item, index: number) => React.ReactNode
}) {
	return (
		<Table
			bordered
			responsive
			headers={["Name", ""]}
			rows={mealPlans.map((mealPlan, index) => [
				<Stack gap="1">
					<Inline gap="2" alignItems="center">
						<Text fontWeight="semibold">{mealPlan.name}</Text>
						{mealPlan.id ? <CodeChip code={String(mealPlan.id)} /> : null}
					</Inline>
					<Inline gap="1" alignItems="center">
						<OtaMealCategoryBadge category={mealPlan.meal_category} />
						{mealPlan.description ? (
							<Text fontSize="sm" color="muted">
								{mealPlan.description}
							</Text>
						) : null}
					</Inline>
				</Stack>,
				renderActions ? renderActions(mealPlan, index) : null,
			])}
		/>
	)
}

export function CodeChip({ code }: { code: string }) {
	if (!code) return null
	return (
		<Box
			padding="1"
			bgColor="primary"
			color="primary"
			rounded="md"
			fontSize="sm"
			fontWeight="semibold"
			whiteSpace="nowrap"
			style={{ height: "fit-content" }}
		>
			Code: {code}
		</Box>
	)
}

function RowActionsMenu({
	itemType,
	itemName,
	onEdit,
	onDelete,
}: {
	itemType: string
	itemName: string
	onEdit: () => void
	onDelete: () => void | Promise<void>
}) {
	return (
		<Inline gap="2" justifyContent="end">
			<Button
				size="sm"
				level="tertiary"
				onClick={onEdit}
				title={`Edit ${itemType}`}
			>
				<Icons.Pencil />
			</Button>
			<ConfirmationDialogProvider>
				{({ confirm }) => (
					<>
						<Dropdown alignRight>
							<Dropdown.ToggleButton size="sm" level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem
									color="danger"
									onClick={() => {
										confirm().then((yes) => {
											if (!yes) return
											return onDelete()
										})
									}}
								>
									<Icons.Ban /> Delete {itemType}
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
						<ConfirmationDialog
							title={`Delete ${itemType}`}
							destructiveAction
							acceptActionLabel="Yes, remove"
						>
							<Alert status="error">
								Are you sure you want to Delete{" "}
								<Text as="span" fontWeight="semibold">
									{itemName}
								</Text>
								?
							</Alert>
						</ConfirmationDialog>
					</>
				)}
			</ConfirmationDialogProvider>
		</Inline>
	)
}

function MealPlanRowActions({
	hotel,
	mealPlan,
	onChange,
}: {
	hotel: IHotel
	mealPlan: TOtaMealPlan
	onChange: () => void
}) {
	const xhr = useXHR()
	const [isEditOpen, showEdit, hideEdit] = useDialog()
	return (
		<>
			<RowActionsMenu
				itemType="Meal Plan"
				itemName={mealPlan.name}
				onEdit={showEdit}
				onDelete={async () => {
					try {
						const { message } = await hotelOtaMealPlansXHR(
							xhr
						).deleteOtaMealPlan(mealPlan.id)
						showSnackbar(message)
						onChange()
					} catch (e) {
						showSnackbar(
							(e as Error).message || "Something went wrong. Please try later."
						)
					}
				}}
			/>
			<OtaMealPlanInDialog
				notice={
					<HotelConfigActionRequiredAlert
						afterAction="updating this Meal Plan"
						rateSheetStep="include this new Meal Plan"
						hasChannelManager={Boolean(hotel.channel_manager)}
					/>
				}
				unlockCopy={{
					title: "Edit Meal Plan",
					action: "Edit Meal Plan",
					afterAction: "updating this Meal Plan",
					rateSheetStep: "include this new Meal Plan",
					acceptLabel: "Yes, unlock meal plan configuration",
					hasChannelManager: Boolean(hotel.channel_manager),
				}}
				mealPlan={mealPlan}
				open={isEditOpen}
				onClose={hideEdit}
				onSubmit={async (data) => {
					await hotelOtaMealPlansXHR(xhr).updateOtaMealPlan(
						hotel.id,
						mealPlan.id,
						data
					)
					showSnackbar("Meal plan updated successfully")
					hideEdit()
					onChange()
				}}
			/>
		</>
	)
}
